'use client'

import { getDefaultMailBoxesValue } from './helpers/getDefaultValue'
import { SignalRNotificationKey } from '../signal-r-provider/SignalRNotificationKey'
import { SignalRContext, SignalRMessageProps } from '../signal-r-provider/signal-r-provider'
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

/**
 * @property mailBoxes - Notifications organised by SignalRNotificationKey
 * @property removeItem - Function to remove a notification from a specific mail box
 */
export type MailBoxProviderValue = {
    mailBoxes: Record<SignalRNotificationKey, SignalRMessageProps[]>
    removeItem: (index: number, mailKey: SignalRNotificationKey) => void
    clearMailbox: (mailKey: SignalRNotificationKey) => void
}

export const MailBoxContext = createContext<MailBoxProviderValue>({
    mailBoxes: getDefaultMailBoxesValue(),
    removeItem: (index: number, mailKey: SignalRNotificationKey) => {},
    clearMailbox: (mailKey: SignalRNotificationKey) => {},
})

/**
 * This provider is responsible for
 * * listening the notifications in signal-r-provider
 * * sorting the notifications into mail boxes
 * * emptying the mailbox on each new animation frame
 */
export const MailBoxProvider = ({ children }: { children: ReactNode }) => {
    const { signalRNotifications, removeNotifications } = useContext(SignalRContext)
    const [mailBoxes, setMailBoxes] = useState<Record<SignalRNotificationKey, SignalRMessageProps[]>>(
        getDefaultMailBoxesValue(),
    )

    useEffect(() => {
        if (!!signalRNotifications.length) {
            sortNewNotifications(signalRNotifications)
            removeNotifications(signalRNotifications.length)
        }
    }, [signalRNotifications])

    const sortNewNotifications = (signalRNotifications: SignalRMessageProps[]) => {
        const newMailBox = getDefaultMailBoxesValue()

        signalRNotifications.forEach((mailItem) => {
            if (mailItem.keyType in newMailBox) {
                newMailBox[mailItem.keyType].push(mailItem)
            } else {
                newMailBox[SignalRNotificationKey.None] = [mailItem]
            }
        })

        /**
         * old comment: Empty the mail boxes on every new animation frame.
         * old version of code:
         * requestAnimationFrame(() => setMailBoxes(getDefaultMailBoxesValue()))
         * @todo
         * TODO: remove this code block, when the hypothetical in the comment below has proven to be rare.
         */

        /**
         * Wait an animation frame so callback functions inside useEffects can act on the new
         * messages. Then on the following animation frame, clear all mail boxes
         *
         * hypothetically, if messages are received within the ~16 miliseconds of the first animation frame,
         * they wil be deleted without triggering an action. This would be rare.
         *
         * The same would happen if we don't wait this extra animation frame and that has proven to be more
         * common. In this case, the callback function of the requestAnimationFrame, would compete
         * with the callback function of useEffects hooks
         * */
        requestAnimationFrame(() => {
            requestAnimationFrame(() => setMailBoxes(getDefaultMailBoxesValue()))
        })

        setMailBoxes(newMailBox)
    }

    /**
     * Remove mail from the post office.
     *
     * @param index The index to remove from the mailbox.
     * @param mailKey The mailbox to remove the index from.
     */
    const removeItem = (index: number, mailKey: SignalRNotificationKey) => {
        if (!(mailKey in mailBoxes)) return

        const mailBox = [...mailBoxes[mailKey]]

        if (mailBox.length >= index) {
            mailBox.splice(index)

            setMailBoxes({ ...mailBoxes, [mailKey]: mailBox })
        }
    }

    /**
     * Clear mailbox by mailbox key
     *
     * @param mailKey The mailbox to clear
     */
    const clearMailbox = (mailKey: SignalRNotificationKey) => {
        if (!(mailKey in mailBoxes)) return

        setMailBoxes({ ...mailBoxes, [mailKey]: [] })
    }

    const providerValue = useMemo(
        () => ({
            mailBoxes,
            removeItem,
            clearMailbox,
        }),
        [mailBoxes, signalRNotifications],
    )

    return <MailBoxContext.Provider value={providerValue}>{children}</MailBoxContext.Provider>
}
